import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, Alert, LinearProgress,
  Stack, Chip, Divider, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, IconButton, Tooltip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, Switch,
  Collapse,
} from '@mui/material';
import {
  AdminPanelSettings as AdminIcon,
  PersonAdd as PersonAddIcon,
  CheckCircle as CheckCircleIcon,
  Block as BlockIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Shield as ShieldIcon,
  Edit as EditIcon,
  Logout as LogoutIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  ContentCopy as ContentCopyIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { authApi, executiveAssignmentsApi, auditLogsApi, permissionsApi } from '../api/services';
import type { EffectivePermissions } from '../api/services';
import apiClient from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import type { ExecutiveAssignment, AuditLogEntry } from '../types/models';

interface UserInfo {
  id: string;
  email: string;
  displayName: string;
  role: string;
  isActive: boolean;
  isTenantAdmin: boolean;
  createdAt: string;
  lastLoginAt: string | null;
  mfaEnabled: boolean;
}

// ---- User Management Section ----
function UserManagementSection() {
  const { user } = useAuth();
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [editUser, setEditUser] = useState<UserInfo | null>(null);
  const [editForm, setEditForm] = useState({ displayName: '', role: '' });
  const [editLoading, setEditLoading] = useState(false);

  // Permissions dialog state
  const [permUser, setPermUser] = useState<UserInfo | null>(null);
  const [permLoading, setPermLoading] = useState(false);
  const [permSaving, setPermSaving] = useState(false);
  const [permError, setPermError] = useState<string | null>(null);
  const [permForm, setPermForm] = useState<Record<string, boolean | null>>({
    canManageStrategicDrivers: null,
    canManageProjects: null,
    canManageBilling: null,
    canManageUsers: null,
    canViewReports: null,
    canManageWebhooks: null,
    canManagePrompts: null,
    canExportData: null,
  });
  const [permEffective, setPermEffective] = useState<EffectivePermissions | null>(null);

  const loadUsers = useCallback(async () => {
    try {
      const data = await authApi.getUsers();
      setUsers(data);
    } catch (err: any) {
      setError(err.response?.status === 403 ? 'Insufficient permissions' : 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handleToggleActive = async (id: string) => {
    try {
      await authApi.toggleUserActive(id);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    }
  };

  const handleOpenEdit = (u: UserInfo) => {
    setEditUser(u);
    setEditForm({ displayName: u.displayName, role: u.role });
  };

  const handleSaveEdit = async () => {
    if (!editUser) return;
    setEditLoading(true);
    try {
      await apiClient.put(`/auth/users/${editUser.id}`, editForm);
      setEditUser(null);
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleToggleTenantAdmin = async (id: string, current: boolean) => {
    try {
      await apiClient.put(`/auth/users/${id}/tenant-admin`, { isTenantAdmin: !current });
      loadUsers();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update tenant admin status');
    }
  };

  const handleForceLogout = async (u: UserInfo) => {
    if (!window.confirm(`Force logout all sessions for ${u.displayName}?`)) return;
    try {
      const result = await authApi.revokeUserSessions(u.id);
      setError(null);
      alert(result.message || 'Sessions revoked.');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to revoke sessions');
    }
  };

  const handleOpenPermissions = async (u: UserInfo) => {
    setPermUser(u);
    setPermLoading(true);
    setPermError(null);
    try {
      const data = await permissionsApi.getUserPermissions(u.id);
      setPermEffective(data.effective);
      if (data.overrides) {
        setPermForm({
          canManageStrategicDrivers: data.overrides.canManageStrategicDrivers,
          canManageProjects: data.overrides.canManageProjects,
          canManageBilling: data.overrides.canManageBilling,
          canManageUsers: data.overrides.canManageUsers,
          canViewReports: data.overrides.canViewReports,
          canManageWebhooks: data.overrides.canManageWebhooks,
          canManagePrompts: data.overrides.canManagePrompts,
          canExportData: data.overrides.canExportData,
        });
      } else {
        setPermForm({
          canManageStrategicDrivers: null,
          canManageProjects: null,
          canManageBilling: null,
          canManageUsers: null,
          canViewReports: null,
          canManageWebhooks: null,
          canManagePrompts: null,
          canExportData: null,
        });
      }
    } catch (err: any) {
      setPermError(err.response?.data?.error || 'Failed to load permissions');
    } finally {
      setPermLoading(false);
    }
  };

  const handleSavePermissions = async () => {
    if (!permUser) return;
    setPermSaving(true);
    setPermError(null);
    try {
      const result = await permissionsApi.updateUserPermissions(permUser.id, permForm);
      setPermEffective(result.effective);
      setPermUser(null);
    } catch (err: any) {
      setPermError(err.response?.data?.error || 'Failed to save permissions');
    } finally {
      setPermSaving(false);
    }
  };

  const permissionLabels: Record<string, string> = {
    canManageStrategicDrivers: 'Manage Strategic Drivers',
    canManageProjects: 'Manage Projects',
    canManageBilling: 'Manage Billing',
    canManageUsers: 'Manage Users',
    canViewReports: 'View Reports',
    canManageWebhooks: 'Manage Webhooks',
    canManagePrompts: 'Manage Prompts',
    canExportData: 'Export Data',
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%&*';
    let pw = '';
    const arr = new Uint8Array(20);
    crypto.getRandomValues(arr);
    for (let i = 0; i < 20; i++) pw += chars[arr[i] % chars.length];
    return pw;
  };

  const handleAddUser = async () => {
    if (!newUser.email || !newUser.displayName || !newUser.password) {
      setAddError('All fields are required');
      return;
    }
    setAddLoading(true);
    setAddError(null);
    try {
      await authApi.registerUser({
        email: newUser.email,
        password: newUser.password,
        displayName: newUser.displayName,
        tenantId: user?.tenantId || '',
        role: newUser.role,
      });
      setShowAdd(false);
      setNewUser({ email: '', password: '', displayName: '', role: 'ExecutiveAssistant' });
      loadUsers();
    } catch (err: any) {
      const errors = err.response?.data?.errors;
      setAddError(errors ? (Array.isArray(errors) ? errors.join(', ') : String(errors)) : 'Failed to create user');
    } finally {
      setAddLoading(false);
    }
  };

  if (loading) return <LinearProgress />;
  if (error && users.length === 0) return <Alert severity="error">{error}</Alert>;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">User Management</Typography>
          <Button variant="outlined" size="small" startIcon={<PersonAddIcon />} onClick={() => setShowAdd(true)}>
            Add User
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Tenant Admin</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>MFA</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map(u => (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {u.displayName}
                      {u.id === user?.id && <Chip label="You" size="small" sx={{ ml: 1, height: 20 }} />}
                    </Typography>
                  </TableCell>
                  <TableCell>{u.email}</TableCell>
                  <TableCell><Chip label={u.role === 'ExecutiveAssistant' ? 'EA' : u.role} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Tooltip title={u.id === user?.id ? 'Cannot change your own status' : (u.isTenantAdmin ? 'Remove tenant admin' : 'Make tenant admin')}>
                      <span>
                        <Switch
                          size="small"
                          checked={u.isTenantAdmin}
                          disabled={u.id === user?.id}
                          onChange={() => handleToggleTenantAdmin(u.id, u.isTenantAdmin)}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={u.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={u.isActive ? 'success' : 'error'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {u.mfaEnabled ? <Chip label="Enabled" size="small" color="success" /> : <Chip label="Off" size="small" variant="outlined" />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {u.id !== user?.id && (
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="Permissions">
                          <IconButton size="small" onClick={() => handleOpenPermissions(u)}>
                            <ShieldIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => handleOpenEdit(u)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={u.isActive ? 'Deactivate user' : 'Reactivate user'}>
                          <IconButton size="small" onClick={() => handleToggleActive(u.id)}>
                            {u.isActive ? <BlockIcon fontSize="small" /> : <CheckCircleIcon fontSize="small" />}
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Force logout all sessions">
                          <IconButton size="small" color="warning" onClick={() => handleForceLogout(u)}>
                            <LogoutIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add New User</DialogTitle>
          <DialogContent>
            {addError && <Alert severity="error" sx={{ mb: 2 }}>{addError}</Alert>}
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Display Name" fullWidth value={newUser.displayName} onChange={e => setNewUser(prev => ({ ...prev, displayName: e.target.value }))} />
              <TextField label="Email" type="email" fullWidth value={newUser.email} onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))} />
              <Stack direction="row" spacing={1}>
                <TextField label="Password" fullWidth value={newUser.password} onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))} helperText="Min 8 chars, upper, lower, digit, special" />
                <Button variant="text" size="small" sx={{ whiteSpace: 'nowrap' }} onClick={() => setNewUser(prev => ({ ...prev, password: generatePassword() }))}>Generate</Button>
              </Stack>
              <TextField label="Role" select fullWidth value={newUser.role} onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value }))}>
                <MenuItem value="Executive">Executive</MenuItem>
                <MenuItem value="ExecutiveAssistant">Executive Assistant</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleAddUser} disabled={addLoading}>
              {addLoading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={!!editUser} onClose={() => setEditUser(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit User — {editUser?.email}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Display Name" fullWidth value={editForm.displayName} onChange={e => setEditForm(prev => ({ ...prev, displayName: e.target.value }))} />
              <TextField label="Role" select fullWidth value={editForm.role} onChange={e => setEditForm(prev => ({ ...prev, role: e.target.value }))}>
                <MenuItem value="Executive">Executive</MenuItem>
                <MenuItem value="ExecutiveAssistant">Executive Assistant</MenuItem>
              </TextField>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditUser(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={editLoading}>
              {editLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Permissions Dialog */}
        <Dialog open={!!permUser} onClose={() => setPermUser(null)} maxWidth="sm" fullWidth>
          <DialogTitle>
            <Stack direction="row" alignItems="center" gap={1}>
              <ShieldIcon color="primary" />
              Permissions — {permUser?.displayName}
            </Stack>
          </DialogTitle>
          <DialogContent>
            {permError && <Alert severity="error" sx={{ mb: 2 }}>{permError}</Alert>}
            {permLoading ? (
              <LinearProgress />
            ) : (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Toggle permissions on or off. Use the middle position (--) to inherit from the user's role default.
                </Typography>
                {Object.entries(permissionLabels).map(([key, label]) => {
                  const value = permForm[key];
                  const effectiveValue = permEffective ? (permEffective as any)[key] : false;
                  return (
                    <Stack key={key} direction="row" alignItems="center" justifyContent="space-between" sx={{ py: 0.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>{label}</Typography>
                        {value === null && (
                          <Typography variant="caption" color="text.secondary">
                            Role default: {effectiveValue ? 'Allowed' : 'Denied'}
                          </Typography>
                        )}
                      </Box>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Tooltip title="Use role default">
                          <Chip
                            label="--"
                            size="small"
                            variant={value === null ? 'filled' : 'outlined'}
                            color={value === null ? 'primary' : 'default'}
                            onClick={() => setPermForm(prev => ({ ...prev, [key]: null }))}
                            sx={{ cursor: 'pointer', minWidth: 36 }}
                          />
                        </Tooltip>
                        <Tooltip title="Deny">
                          <Chip
                            label="No"
                            size="small"
                            variant={value === false ? 'filled' : 'outlined'}
                            color={value === false ? 'error' : 'default'}
                            onClick={() => setPermForm(prev => ({ ...prev, [key]: false }))}
                            sx={{ cursor: 'pointer', minWidth: 36 }}
                          />
                        </Tooltip>
                        <Tooltip title="Allow">
                          <Chip
                            label="Yes"
                            size="small"
                            variant={value === true ? 'filled' : 'outlined'}
                            color={value === true ? 'success' : 'default'}
                            onClick={() => setPermForm(prev => ({ ...prev, [key]: true }))}
                            sx={{ cursor: 'pointer', minWidth: 36 }}
                          />
                        </Tooltip>
                      </Stack>
                    </Stack>
                  );
                })}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPermUser(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSavePermissions} disabled={permSaving || permLoading}>
              {permSaving ? 'Saving...' : 'Save Permissions'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---- Executive Assignments Section ----
function ExecutiveAssignmentsSection() {
  const [assignments, setAssignments] = useState<ExecutiveAssignment[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [newAssignment, setNewAssignment] = useState({ executiveUserId: '', assistantUserId: '' });
  const [addLoading, setAddLoading] = useState(false);

  const load = useCallback(async () => {
    try {
      const [assignmentsData, usersData] = await Promise.all([
        executiveAssignmentsApi.getAll(),
        authApi.getUsers(),
      ]);
      setAssignments(assignmentsData);
      setUsers(usersData);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    if (!newAssignment.executiveUserId || !newAssignment.assistantUserId) return;
    setAddLoading(true);
    try {
      await executiveAssignmentsApi.create(newAssignment);
      setShowAdd(false);
      setNewAssignment({ executiveUserId: '', assistantUserId: '' });
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to create assignment');
    } finally {
      setAddLoading(false);
    }
  };

  const handleDeactivate = async (id: string) => {
    try {
      await executiveAssignmentsApi.deactivate(id);
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to deactivate');
    }
  };

  const getUserName = (id: string) => {
    const u = users.find(u => u.id === id);
    return u ? `${u.displayName} (${u.email})` : id;
  };

  const executives = users.filter(u => u.role === 'Executive');
  const assistants = users.filter(u => u.role === 'ExecutiveAssistant');

  if (loading) return <LinearProgress />;

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Typography variant="h6">Executive Assignments</Typography>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setShowAdd(true)}>
            New Assignment
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Executive</TableCell>
                <TableCell>Assistant</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map(a => (
                <TableRow key={a.id} hover>
                  <TableCell>{getUserName(a.executiveUserId)}</TableCell>
                  <TableCell>{getUserName(a.assistantUserId)}</TableCell>
                  <TableCell>
                    <Chip
                      label={a.isActive ? 'Active' : 'Inactive'}
                      size="small"
                      color={a.isActive ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(a.assignedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {a.isActive && (
                      <Tooltip title="Deactivate assignment">
                        <IconButton size="small" onClick={() => handleDeactivate(a.id)}>
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {assignments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 2 }}>No assignments</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>New Executive Assignment</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <FormControl fullWidth>
                <InputLabel>Executive</InputLabel>
                <Select
                  value={newAssignment.executiveUserId}
                  label="Executive"
                  onChange={e => setNewAssignment(prev => ({ ...prev, executiveUserId: e.target.value }))}
                >
                  {executives.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.displayName} ({u.email})</MenuItem>
                  ))}
                  {executives.length === 0 && (
                    <MenuItem disabled>No users with Executive role</MenuItem>
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Assistant</InputLabel>
                <Select
                  value={newAssignment.assistantUserId}
                  label="Assistant"
                  onChange={e => setNewAssignment(prev => ({ ...prev, assistantUserId: e.target.value }))}
                >
                  {assistants.map(u => (
                    <MenuItem key={u.id} value={u.id}>{u.displayName} ({u.email})</MenuItem>
                  ))}
                  {assistants.length === 0 && (
                    <MenuItem disabled>No users with EA role</MenuItem>
                  )}
                </Select>
              </FormControl>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={addLoading}>
              {addLoading ? 'Creating...' : 'Create Assignment'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---- Audit Log Section ----
function AuditLogSection() {
  const [entries, setEntries] = useState<AuditLogEntry[]>([]);
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const userNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    users.forEach(u => { map[u.id] = u.displayName || u.email; });
    return map;
  }, [users]);

  const loadEntries = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {};
      if (actionFilter) params.action = actionFilter;
      if (dateFrom) params.from = dateFrom;
      if (dateTo) params.to = dateTo;
      const data = await auditLogsApi.getAll(params);
      setEntries(data);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [actionFilter, dateFrom, dateTo]);

  useEffect(() => { loadEntries(); }, [loadEntries]);

  // Load users for name lookup
  useEffect(() => {
    authApi.getUsers().then(setUsers).catch(() => {});
  }, []);

  const uniqueActions = Array.from(new Set(entries.map(e => e.action))).sort();

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2 }}>Audit Log</Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

        {/* Filters */}
        <Stack direction="row" spacing={2} sx={{ mb: 2 }} alignItems="center">
          <FilterIcon color="action" />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action</InputLabel>
            <Select value={actionFilter} label="Action" onChange={e => setActionFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {uniqueActions.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={e => setDateFrom(e.target.value)}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={e => setDateTo(e.target.value)}
          />
        </Stack>

        {loading && <LinearProgress sx={{ mb: 1 }} />}

        <TableContainer sx={{ maxHeight: 500 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>User</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Entity</TableCell>
                <TableCell>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {entries.map(entry => (
                <TableRow key={entry.id} hover>
                  <TableCell>
                    <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                      {new Date(entry.timestamp).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{userNameMap[entry.userId] || entry.userId.slice(0, 8) + '...'}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip label={entry.action} size="small" variant="outlined" />
                  </TableCell>
                  <TableCell>
                    {entry.entityType && (
                      <Typography variant="body2">
                        {entry.entityType}
                        {entry.entityId && (
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            ({entry.entityId.substring(0, 8)}...)
                          </Typography>
                        )}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {entry.detail || '-'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
              {!loading && entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography color="text.secondary" sx={{ py: 4 }}>No audit entries found</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
}

// ---- SSO Configuration Section ----
interface SsoConfig {
  id: string;
  provider: string;
  displayName: string | null;
  authority: string | null;
  clientId: string | null;
  hasClientSecret: boolean;
  metadataUrl: string | null;
  emailDomain: string | null;
  isEnabled: boolean;
  enforceSso: boolean;
  createdAt: string;
  updatedAt: string;
}

function SsoConfigSection() {
  const [configs, setConfigs] = useState<SsoConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showAdd, setShowAdd] = useState(false);
  const [editConfig, setEditConfig] = useState<SsoConfig | null>(null);
  const [testResult, setTestResult] = useState<{
    id: string; success: boolean; message: string;
    metadataReachable?: boolean; hasAuthEndpoint?: boolean; hasTokenEndpoint?: boolean; issuerMatch?: boolean;
  } | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [guideExpanded, setGuideExpanded] = useState<string | null>(null);
  const [copiedCallback, setCopiedCallback] = useState(false);
  const [newConfig, setNewConfig] = useState({
    provider: 'AzureAD',
    displayName: '',
    authority: '',
    clientId: '',
    clientSecret: '',
    metadataUrl: '',
    emailDomain: '',
  });
  const [editForm, setEditForm] = useState({
    provider: '',
    displayName: '',
    authority: '',
    clientId: '',
    clientSecret: '',
    metadataUrl: '',
    emailDomain: '',
    isEnabled: false,
    enforceSso: false,
  });

  const loadConfigs = useCallback(async () => {
    try {
      const { data } = await apiClient.get<SsoConfig[]>('/sso/config');
      setConfigs(data);
      setError(null);
    } catch (err: any) {
      if (err.response?.status === 403) {
        // SSO not enabled for this tenant — show nothing
        setConfigs([]);
        setError('SSO is not available on your current plan. Upgrade to Professional or Enterprise to enable SSO.');
      } else {
        setError(err.response?.data?.error || 'Failed to load SSO configurations');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfigs(); }, [loadConfigs]);

  const handleCreate = async () => {
    if (!newConfig.emailDomain || !newConfig.provider) return;
    setFormLoading(true);
    setError(null);
    try {
      await apiClient.post('/sso/config', newConfig);
      setShowAdd(false);
      setNewConfig({ provider: 'AzureAD', displayName: '', authority: '', clientId: '', clientSecret: '', metadataUrl: '', emailDomain: '' });
      setSuccess('SSO configuration created. Test it before enabling.');
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create SSO configuration');
    } finally {
      setFormLoading(false);
    }
  };

  const handleOpenEdit = (c: SsoConfig) => {
    setEditConfig(c);
    setEditForm({
      provider: c.provider,
      displayName: c.displayName || '',
      authority: c.authority || '',
      clientId: c.clientId || '',
      clientSecret: '', // Don't pre-fill secret
      metadataUrl: c.metadataUrl || '',
      emailDomain: c.emailDomain || '',
      isEnabled: c.isEnabled,
      enforceSso: c.enforceSso,
    });
  };

  const handleSaveEdit = async () => {
    if (!editConfig) return;
    setFormLoading(true);
    setError(null);
    try {
      const payload: any = { ...editForm };
      // Only send clientSecret if it was changed
      if (!payload.clientSecret) delete payload.clientSecret;
      await apiClient.put(`/sso/config/${editConfig.id}`, payload);
      setEditConfig(null);
      setSuccess('SSO configuration updated.');
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update SSO configuration');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this SSO configuration? Users will no longer be able to sign in via SSO for this domain.')) return;
    try {
      await apiClient.delete(`/sso/config/${id}`);
      setSuccess('SSO configuration deleted.');
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete SSO configuration');
    }
  };

  const handleTest = async (id: string) => {
    setTestResult(null);
    try {
      const { data } = await apiClient.post(`/sso/config/${id}/test`);
      setTestResult({
        id,
        success: data.success,
        message: data.success ? data.message : data.error,
        metadataReachable: data.metadataReachable,
        hasAuthEndpoint: data.hasAuthEndpoint,
        hasTokenEndpoint: data.hasTokenEndpoint,
        issuerMatch: data.issuerMatch,
      });
    } catch (err: any) {
      setTestResult({ id, success: false, message: err.response?.data?.error || 'Test failed' });
    }
  };

  const handleCopyCallback = () => {
    navigator.clipboard.writeText(callbackUrl);
    setCopiedCallback(true);
    setTimeout(() => setCopiedCallback(false), 2000);
  };

  const getProviderConsoleUrl = (provider: string) => {
    switch (provider) {
      case 'AzureAD': return 'https://portal.azure.com/#view/Microsoft_AAD_IAM/ActiveDirectoryMenuBlade/~/RegisteredApps';
      case 'Okta': return 'https://login.okta.com/';
      case 'Google': return 'https://console.cloud.google.com/apis/credentials';
      default: return null;
    }
  };

  const handleToggleEnabled = async (c: SsoConfig) => {
    try {
      await apiClient.put(`/sso/config/${c.id}`, { isEnabled: !c.isEnabled });
      loadConfigs();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update SSO configuration');
    }
  };

  if (loading) return <LinearProgress />;

  const providerOptions = [
    { value: 'AzureAD', label: 'Azure AD / Entra ID' },
    { value: 'Okta', label: 'Okta' },
    { value: 'Google', label: 'Google Workspace' },
    { value: 'OIDC', label: 'Custom OIDC' },
  ];

  const getAuthorityHint = (provider: string) => {
    switch (provider) {
      case 'AzureAD': return 'https://login.microsoftonline.com/{tenant-id}/v2.0';
      case 'Okta': return 'https://{your-domain}.okta.com/oauth2/default';
      case 'Google': return 'https://accounts.google.com';
      default: return 'https://your-idp.com';
    }
  };

  const callbackUrl = `${process.env.REACT_APP_API_URL?.replace(/\/api$/, '') || 'https://api.stratiumhq.com'}/api/sso/callback`;

  const getProviderSetupSteps = (provider: string): string[] => {
    switch (provider) {
      case 'AzureAD': return [
        '1. Go to Azure Portal > App Registrations > New Registration',
        `2. Set redirect URI to: ${callbackUrl}`,
        '3. Copy the Application (client) ID and Directory (tenant) ID',
        '4. Create a client secret under Certificates & Secrets',
        '5. Set Authority URL below to: https://login.microsoftonline.com/{tenant-id}/v2.0',
        '6. Enter the Client ID and Client Secret',
        '7. Set Email Domain to your company domain, then Test the configuration',
      ];
      case 'Okta': return [
        '1. Go to Okta Admin Console > Applications > Create App Integration',
        '2. Select OIDC - OpenID Connect, then Web Application',
        `3. Set Sign-in redirect URI to: ${callbackUrl}`,
        '4. Copy the Client ID and Client Secret',
        '5. Set Authority URL below to: https://{your-domain}.okta.com/oauth2/default',
        '6. Enter the Client ID and Client Secret',
        '7. Set Email Domain to your company domain, then Test the configuration',
      ];
      case 'Google': return [
        '1. Go to Google Cloud Console > APIs & Services > Credentials',
        '2. Create an OAuth 2.0 Client ID (Web application type)',
        `3. Add authorized redirect URI: ${callbackUrl}`,
        '4. Copy the Client ID and Client Secret',
        '5. Set Authority URL below to: https://accounts.google.com',
        '6. Enter the Client ID and Client Secret',
        '7. Set Email Domain to your Google Workspace domain, then Test the configuration',
      ];
      default: return [
        '1. Obtain the OIDC discovery URL from your identity provider',
        `2. Set the redirect/callback URI to: ${callbackUrl}`,
        '3. Register a client application and obtain Client ID + Secret',
        '4. Enter the Authority URL, Client ID, and Client Secret below',
        '5. Set Email Domain to your company domain, then Test the configuration',
      ];
    }
  };

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <SecurityIcon color="primary" />
            <Typography variant="h6">Single Sign-On (SSO)</Typography>
          </Stack>
          <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => setShowAdd(true)}>
            Add SSO
          </Button>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {configs.length === 0 && !error ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No SSO configurations. Add one to enable federated authentication for your organisation.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Display Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Enforce</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {configs.map(c => (
                  <React.Fragment key={c.id}>
                  <TableRow hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500}>{c.emailDomain}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={c.provider} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>{c.displayName || '-'}</TableCell>
                    <TableCell>
                      <Switch
                        size="small"
                        checked={c.isEnabled}
                        onChange={() => handleToggleEnabled(c)}
                      />
                      <Chip
                        label={c.isEnabled ? 'Active' : 'Disabled'}
                        size="small"
                        color={c.isEnabled ? 'success' : 'default'}
                        variant="outlined"
                        sx={{ ml: 0.5 }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={c.enforceSso ? 'Required' : 'Optional'}
                        size="small"
                        color={c.enforceSso ? 'warning' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end">
                        <Tooltip title="Test configuration">
                          <IconButton size="small" color="info" onClick={() => handleTest(c.id)}>
                            <VerifiedUserIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => handleOpenEdit(c)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" color="error" onClick={() => handleDelete(c.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                      {testResult?.id === c.id && (
                        <Alert severity={testResult.success ? 'success' : 'error'} sx={{ mt: 1, fontSize: 12 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>{testResult.message}</Typography>
                          {testResult.metadataReachable !== undefined && (
                            <Box sx={{ mt: 0.5 }}>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Metadata reachable: {testResult.metadataReachable ? 'Yes' : 'No'}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Authorization endpoint: {testResult.hasAuthEndpoint ? 'Found' : 'Missing'}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Token endpoint: {testResult.hasTokenEndpoint ? 'Found' : 'Missing'}
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block' }}>
                                Issuer match: {testResult.issuerMatch ? 'Yes' : 'No'}
                              </Typography>
                            </Box>
                          )}
                        </Alert>
                      )}
                    </TableCell>
                  </TableRow>
                  {/* Collapsible setup guide row */}
                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: guideExpanded === c.id ? undefined : 'none' }}>
                      <Button
                        size="small"
                        onClick={() => setGuideExpanded(guideExpanded === c.id ? null : c.id)}
                        endIcon={guideExpanded === c.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        sx={{ textTransform: 'none', mb: guideExpanded === c.id ? 0 : 0 }}
                      >
                        {guideExpanded === c.id ? 'Hide' : 'Show'} setup guide for {c.provider}
                      </Button>
                      <Collapse in={guideExpanded === c.id}>
                        <Box sx={{ py: 1.5, px: 1 }}>
                          <Stack spacing={1}>
                            {getProviderSetupSteps(c.provider).map((step, i) => (
                              <Typography key={i} variant="body2" sx={{ fontSize: '0.82rem', lineHeight: 1.6, fontFamily: step.includes('://') ? 'monospace' : 'inherit' }}>
                                {step}
                              </Typography>
                            ))}
                            <Divider sx={{ my: 1 }} />
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                                Callback URL: {callbackUrl}
                              </Typography>
                              <Tooltip title={copiedCallback ? 'Copied' : 'Copy callback URL'}>
                                <IconButton size="small" onClick={handleCopyCallback}>
                                  <ContentCopyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                            {getProviderConsoleUrl(c.provider) && (
                              <Button
                                size="small"
                                variant="text"
                                startIcon={<OpenInNewIcon />}
                                href={getProviderConsoleUrl(c.provider)!}
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ textTransform: 'none', alignSelf: 'flex-start' }}
                              >
                                Open {c.provider === 'AzureAD' ? 'Azure Portal' : c.provider === 'Okta' ? 'Okta Admin Console' : c.provider === 'Google' ? 'Google Cloud Console' : 'Provider Console'}
                              </Button>
                            )}
                          </Stack>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Add SSO Dialog */}
        <Dialog open={showAdd} onClose={() => setShowAdd(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Add SSO Configuration</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Provider"
                select
                fullWidth
                value={newConfig.provider}
                onChange={e => setNewConfig(prev => ({ ...prev, provider: e.target.value }))}
              >
                {providerOptions.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
              <Box sx={{ border: 1, borderColor: 'info.main', borderRadius: 1, overflow: 'hidden' }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setGuideExpanded(guideExpanded === 'add' ? null : 'add')}
                  endIcon={guideExpanded === 'add' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'info.main', color: 'info.contrastText', '&:hover': { bgcolor: 'info.dark' } }}
                >
                  Setup guide for {providerOptions.find(p => p.value === newConfig.provider)?.label || newConfig.provider}
                </Button>
                <Collapse in={guideExpanded === 'add'}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    {getProviderSetupSteps(newConfig.provider).map((step, i) => (
                      <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.7 }}>{step}</Typography>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        Callback URL: {callbackUrl}
                      </Typography>
                      <Tooltip title={copiedCallback ? 'Copied' : 'Copy'}>
                        <IconButton size="small" onClick={handleCopyCallback}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {getProviderConsoleUrl(newConfig.provider) && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<OpenInNewIcon />}
                        href={getProviderConsoleUrl(newConfig.provider)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', mt: 0.5 }}
                      >
                        Open {newConfig.provider === 'AzureAD' ? 'Azure Portal' : newConfig.provider === 'Okta' ? 'Okta Admin Console' : 'Google Cloud Console'}
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </Box>
              <TextField
                label="Email Domain"
                fullWidth
                required
                value={newConfig.emailDomain}
                onChange={e => setNewConfig(prev => ({ ...prev, emailDomain: e.target.value }))}
                helperText="Users with this email domain will see the SSO login option"
                placeholder="acme.com"
              />
              <TextField
                label="Display Name"
                fullWidth
                value={newConfig.displayName}
                onChange={e => setNewConfig(prev => ({ ...prev, displayName: e.target.value }))}
                helperText="Shown on the login button, e.g. 'Acme Corp SSO'"
              />
              <TextField
                label="Authority URL"
                fullWidth
                value={newConfig.authority}
                onChange={e => setNewConfig(prev => ({ ...prev, authority: e.target.value }))}
                helperText={`OIDC authority URL, e.g. ${getAuthorityHint(newConfig.provider)}`}
              />
              <TextField
                label="Client ID"
                fullWidth
                value={newConfig.clientId}
                onChange={e => setNewConfig(prev => ({ ...prev, clientId: e.target.value }))}
              />
              <TextField
                label="Client Secret"
                fullWidth
                type="password"
                value={newConfig.clientSecret}
                onChange={e => setNewConfig(prev => ({ ...prev, clientSecret: e.target.value }))}
              />
              <TextField
                label="Metadata URL (optional)"
                fullWidth
                value={newConfig.metadataUrl}
                onChange={e => setNewConfig(prev => ({ ...prev, metadataUrl: e.target.value }))}
                helperText="Override for .well-known/openid-configuration URL. Leave blank to auto-detect from authority."
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAdd(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleCreate} disabled={formLoading}>
              {formLoading ? 'Creating...' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit SSO Dialog */}
        <Dialog open={!!editConfig} onClose={() => setEditConfig(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit SSO Configuration — {editConfig?.emailDomain}</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField
                label="Provider"
                select
                fullWidth
                value={editForm.provider}
                onChange={e => setEditForm(prev => ({ ...prev, provider: e.target.value }))}
              >
                {providerOptions.map(p => <MenuItem key={p.value} value={p.value}>{p.label}</MenuItem>)}
              </TextField>
              <Box sx={{ border: 1, borderColor: 'info.main', borderRadius: 1, overflow: 'hidden' }}>
                <Button
                  fullWidth
                  size="small"
                  onClick={() => setGuideExpanded(guideExpanded === 'edit' ? null : 'edit')}
                  endIcon={guideExpanded === 'edit' ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ textTransform: 'none', justifyContent: 'space-between', px: 2, py: 1, bgcolor: 'info.main', color: 'info.contrastText', '&:hover': { bgcolor: 'info.dark' } }}
                >
                  Setup guide for {providerOptions.find(p => p.value === editForm.provider)?.label || editForm.provider}
                </Button>
                <Collapse in={guideExpanded === 'edit'}>
                  <Box sx={{ px: 2, py: 1.5 }}>
                    {getProviderSetupSteps(editForm.provider).map((step, i) => (
                      <Typography key={i} variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.7 }}>{step}</Typography>
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        Callback URL: {callbackUrl}
                      </Typography>
                      <Tooltip title={copiedCallback ? 'Copied' : 'Copy'}>
                        <IconButton size="small" onClick={handleCopyCallback}>
                          <ContentCopyIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    {getProviderConsoleUrl(editForm.provider) && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<OpenInNewIcon />}
                        href={getProviderConsoleUrl(editForm.provider)!}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ textTransform: 'none', mt: 0.5 }}
                      >
                        Open {editForm.provider === 'AzureAD' ? 'Azure Portal' : editForm.provider === 'Okta' ? 'Okta Admin Console' : editForm.provider === 'Google' ? 'Google Cloud Console' : 'Provider Console'}
                      </Button>
                    )}
                  </Box>
                </Collapse>
              </Box>
              <TextField
                label="Email Domain"
                fullWidth
                value={editForm.emailDomain}
                onChange={e => setEditForm(prev => ({ ...prev, emailDomain: e.target.value }))}
              />
              <TextField
                label="Display Name"
                fullWidth
                value={editForm.displayName}
                onChange={e => setEditForm(prev => ({ ...prev, displayName: e.target.value }))}
              />
              <TextField
                label="Authority URL"
                fullWidth
                value={editForm.authority}
                onChange={e => setEditForm(prev => ({ ...prev, authority: e.target.value }))}
                helperText={`e.g. ${getAuthorityHint(editForm.provider)}`}
              />
              <TextField
                label="Client ID"
                fullWidth
                value={editForm.clientId}
                onChange={e => setEditForm(prev => ({ ...prev, clientId: e.target.value }))}
              />
              <TextField
                label="Client Secret"
                fullWidth
                type="password"
                value={editForm.clientSecret}
                onChange={e => setEditForm(prev => ({ ...prev, clientSecret: e.target.value }))}
                helperText={editConfig?.hasClientSecret ? 'Leave blank to keep existing secret' : 'Required'}
              />
              <TextField
                label="Metadata URL (optional)"
                fullWidth
                value={editForm.metadataUrl}
                onChange={e => setEditForm(prev => ({ ...prev, metadataUrl: e.target.value }))}
              />
              <Divider />
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Typography variant="body2">Enable SSO</Typography>
                <Switch
                  checked={editForm.isEnabled}
                  onChange={e => setEditForm(prev => ({ ...prev, isEnabled: e.target.checked }))}
                />
              </Stack>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="body2">Enforce SSO</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Disables password login for users with this email domain
                  </Typography>
                </Box>
                <Switch
                  checked={editForm.enforceSso}
                  onChange={e => setEditForm(prev => ({ ...prev, enforceSso: e.target.checked }))}
                />
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditConfig(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveEdit} disabled={formLoading}>
              {formLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>
      </CardContent>
    </Card>
  );
}

// ---- Main Admin Page ----
export default function AdminPage() {
  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
        <AdminIcon sx={{ color: 'primary.main' }} />
        <Box>
          <Typography variant="h4">Admin</Typography>
          <Typography variant="subtitle1" color="text.secondary">
            User management, SSO, assignments, and audit log
          </Typography>
        </Box>
      </Box>

      <Stack spacing={2.5}>
        <UserManagementSection />
        <SsoConfigSection />
        <ExecutiveAssignmentsSection />
        <AuditLogSection />
      </Stack>
    </Box>
  );
}
